import React, { useState } from 'react';
import { message } from 'antd';
import {
  GiftIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { formatReward, pluralize, splitReferralLink } from './referralFormat';

// Koyu hero zemini üzerinde tutarlı stil için paylaşım butonu sınıfı.
const shareBtnCls =
  'inline-flex h-9 items-center gap-1.5 rounded-lg bg-white/15 px-3 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/25';

const ReferralHero = ({ data, onShare }) => {
  const [copied, setCopied] = useState(false);
  const link = data?.referrallink || '';
  const code = data?.referralcode || '';
  const shareText = link || code;
  const { head, tail } = splitReferralLink(shareText);

  const handleCopy = async () => {
    if (!shareText) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      message.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.info('Copy the referral link manually');
    }
  };

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: 'Join me on Thisway',
        text: `Join Thisway and get ${formatReward(data?.rewardamount)} in credits.`,
        url: link,
      });
    } catch {
      // Kullanıcı paylaşım sayfasını kapattı — işlem gerekmez.
    }
  };

  return (
    <section className="reveal overflow-hidden rounded-xl bg-gradient-to-r from-blue-700 to-blue-500 p-6 text-white shadow-sm dark:from-sky-700 dark:to-sky-500 sm:p-8">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
        <GiftIcon className="h-5 w-5" />
        Refer &amp; Earn
      </div>
      <h2 className="mt-2 text-2xl font-bold sm:text-3xl">
        Earn {formatReward(data?.rewardamount)} per referral
      </h2>
      <p className="mt-1 max-w-xl text-sm text-white/80">
        Both restaurants get credit once the new restaurant completes{' '}
        {pluralize(data?.minimumorders, 'paid order')}. Credit is valid for{' '}
        {pluralize(data?.expirationdays, 'day')}.
      </p>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div
          data-testid="referral-link"
          title={shareText || undefined}
          className="flex min-w-0 flex-1 items-center rounded-lg bg-white/15 px-3 py-2 text-sm font-medium ring-1 ring-white/20"
        >
          {/* Middle ellipsis: the head gives way, the tail (the ?ref= code)
              never does; a link that fits shows whole. */}
          <span data-testid="referral-link-head" className="min-w-0 truncate">
            {shareText ? head : 'No referral link available'}
          </span>
          <span data-testid="referral-link-tail" className="shrink-0 whitespace-nowrap">
            {tail}
          </span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!shareText}
          className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-white px-4 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-50"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4" />
          ) : (
            <DocumentDuplicateIcon className="h-4 w-4" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          className={shareBtnCls}
          onClick={() => onShare('sms')}
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          SMS
        </button>
        <button
          type="button"
          className={shareBtnCls}
          onClick={() => onShare('email')}
        >
          <EnvelopeIcon className="h-4 w-4" />
          Email
        </button>
        {canNativeShare ? (
          <button
            type="button"
            className={shareBtnCls}
            onClick={handleNativeShare}
          >
            <ShareIcon className="h-4 w-4" />
            Share
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default ReferralHero;
